import type { EntityKind } from '@parcel/analytics';
import { capture } from '@parcel/analytics/browser';
import {
  type TableFilter,
  type WhereDataset,
  type WhereField,
  type WhereRule,
  isWhereGroup,
  operatorsFor,
  whereField,
  whereFieldName,
} from '@parcel/filters';
import { Popover, PopoverAnchor, PopoverContent, toast } from '@parcel/ui';
import { Filter, Search } from 'lucide-react';
import * as React from 'react';
import { type AskWhereResponse, barNodes, whereFromBarNodes } from '../../lib/table-filter.js';
import { AskChip } from '../filter-bar/ask-chip.js';
import { Chip } from '../filter-bar/filter-chips.js';
import { type RecordMatch, pushRecentField } from '../filter-bar/filter-menu.js';
import { RuleEditorPanel, WhereGroupChip, WhereRuleChip } from './where-chip.js';
import type { WhereOptions } from './where-menu.js';
import { WhereSearchInput } from './where-search-input.js';

export interface WhereBarProps {
  dataset: WhereDataset;
  filter: TableFilter;
  onFilterChange: (next: TableFilter) => void;
  options: WhereOptions;
  recentKey: string;
  placeholder?: string;
  /** Enables Ask; the bar owns the in-flight chip and the notices, the page owns the merge. */
  onAsk?: (query: string) => Promise<AskWhereResponse>;
  askExample?: string;
  records?: readonly RecordMatch[];
  onSelectRecord?: (id: string) => void;
  onInputChange?: (text: string) => void;
}

/** A rule the member picked from the menu and has not finished yet; it joins the filter once whole. */
type Draft = { rule: WhereRule };

/** The table bar: today's pill, menu and chips over `{ q, where }`; each chip is one top-level rule. */
export function WhereBar({
  dataset,
  filter,
  onFilterChange,
  options,
  recentKey,
  placeholder,
  onAsk,
  askExample,
  records,
  onSelectRecord,
  onInputChange,
}: WhereBarProps) {
  const [editing, setEditing] = React.useState<number | null>(null);
  const [draft, setDraft] = React.useState<Draft | null>(null);
  const [pendingAsk, setPendingAsk] = React.useState<string | null>(null);
  const inputRef = React.useRef<HTMLInputElement>(null);
  // Async Ask handlers read the latest filter, never the one from when they started.
  const filterRef = React.useRef(filter);
  filterRef.current = filter;

  const rules = barNodes(filter.where);
  const setRules = (next: TableFilter['where']['rules']) =>
    onFilterChange({ ...filterRef.current, where: whereFromBarNodes(next) });

  const replaceRule = (index: number, rule: WhereRule) =>
    setRules(rules.map((r, i) => (i === index ? rule : r)));
  const removeRule = (index: number) => {
    setEditing(null);
    setRules(rules.filter((_, i) => i !== index));
  };

  const pickField = (field: WhereField) => {
    pushRecentField(window.localStorage, recentKey, field.key);
    const existing = rules.findIndex((r) => !isWhereGroup(r) && r.field === field.key);
    if (existing >= 0) {
      setEditing(existing);
      return;
    }
    setEditing(null);
    setDraft({ rule: { field: field.key, operator: operatorsFor(field)[0] ?? 'in' } });
  };

  // "Project use → Residential" in the typing menu: add to that field's "is" chip, or start one.
  const pickValue = (field: WhereField, value: string) => {
    pushRecentField(window.localStorage, recentKey, field.key);
    const index = rules.findIndex(
      (r) => !isWhereGroup(r) && r.field === field.key && r.operator === 'in',
    );
    if (index >= 0) {
      const rule = rules[index] as WhereRule;
      const current = Array.isArray(rule.value) ? (rule.value as string[]) : [];
      if (!current.includes(value)) replaceRule(index, { ...rule, value: [...current, value] });
      return;
    }
    setRules([...rules, { field: field.key, operator: 'in', value: [value] }]);
  };

  const commitDraft = (rule: WhereRule) => {
    const current = barNodes(filterRef.current.where);
    const index = current.length;
    setDraft(null);
    setRules([...current, rule]);
    // A list field keeps its editor open on the new chip, so more values can be ticked.
    setEditing(whereField(dataset, rule.field)?.kind === 'choice' ? index : null);
  };

  const runAsk = async (query: string) => {
    if (!onAsk || pendingAsk !== null || !query.trim()) return;
    setPendingAsk(query);
    try {
      const result = await onAsk(query);
      capture('ask_submitted', {
        entity: dataset as EntityKind,
        query,
        query_length: query.length,
        produced_filters: result.where.rules.length + (result.q ? 1 : 0),
      });
      if (result.unmapped.length > 0) {
        toast.warning(`Couldn’t translate ${result.unmapped.map((u) => `“${u}”`).join(', ')}`, {
          description: 'Try fields like stage, type, city, or a date.',
        });
      }
    } catch {
      // Translation, network or auth failure: fall back to a plain text search.
      onFilterChange({ ...filterRef.current, q: query });
      toast.error('Couldn’t translate — applied your question as a text search');
    } finally {
      setPendingAsk(null);
    }
  };

  const onBackspaceEmpty = () => {
    if (rules.length > 0) {
      const last = rules.length - 1;
      if (!isWhereGroup(rules[last] as WhereRule)) setEditing(last);
      return;
    }
    if (filter.q.trim()) onFilterChange({ ...filter, q: '' });
  };

  const q = filter.q.trim();
  return (
    <div className="relative flex min-w-0 flex-1 flex-wrap items-center gap-2">
      <WhereSearchInput
        dataset={dataset}
        options={options}
        recentKey={recentKey}
        placeholder={placeholder}
        onPickField={pickField}
        onPickValue={pickValue}
        onSearchText={(text) => onFilterChange({ ...filterRef.current, q: text })}
        onAsk={onAsk ? (query) => void runAsk(query) : undefined}
        askExample={askExample}
        records={records}
        onSelectRecord={onSelectRecord}
        onInputChange={onInputChange}
        onBackspaceEmpty={onBackspaceEmpty}
        inputRef={inputRef}
      />

      {pendingAsk !== null ? <AskChip question={pendingAsk} /> : null}

      {q ? (
        <Chip
          icon={<Search className="h-3 w-3 text-muted-foreground" aria-hidden />}
          name="Search"
          values={[{ value: 'q', label: `“${q}”` }]}
          onName={() => inputRef.current?.focus()}
          onRemove={() => onFilterChange({ ...filter, q: '' })}
        />
      ) : null}

      {rules.map((node, index) =>
        isWhereGroup(node) ? (
          <WhereGroupChip
            // biome-ignore lint/suspicious/noArrayIndexKey: a chip is its position among the rules.
            key={`group-${index}`}
            dataset={dataset}
            group={node}
            onRemove={() => removeRule(index)}
          />
        ) : (
          <WhereRuleChip
            key={`rule-${index}-${node.field}`}
            chipKey={`rule-${index}`}
            dataset={dataset}
            rule={node}
            options={options}
            open={editing === index}
            onOpenChange={(open) => setEditing(open ? index : null)}
            onCommit={(rule) => replaceRule(index, rule)}
            onRemove={() => removeRule(index)}
          />
        ),
      )}

      {draft ? (
        <Popover open onOpenChange={(open) => !open && setDraft(null)}>
          <PopoverAnchor asChild>
            <Chip
              icon={<Filter className="h-3 w-3 text-muted-foreground" aria-hidden />}
              name={whereFieldName(dataset, draft.rule.field)}
              values={[]}
              onName={() => {}}
              onRemove={() => setDraft(null)}
            />
          </PopoverAnchor>
          <PopoverContent className="w-auto p-0" align="start">
            <RuleEditorPanel
              dataset={dataset}
              rule={draft.rule}
              options={options}
              onCommit={commitDraft}
              onRemove={() => setDraft(null)}
              onDone={() => setDraft(null)}
            />
          </PopoverContent>
        </Popover>
      ) : null}
    </div>
  );
}
